import { ReactNode, useState, useEffect } from "react";
import { ChevronRight } from "lucide-react";
import { AppSidebar } from "./AppSidebar";
import { SidebarProvider, SidebarInset, useSidebar } from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";

interface AppLayoutProps {
  children: ReactNode;
}

const SIDEBAR_STORAGE_KEY = "sidebar-collapsed";

function readSidebarState(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== "true";
  } catch {
    return true;
  }
}

function SidebarEdgeTrigger() {
  const { toggleSidebar, state } = useSidebar();
  const collapsed = state === "collapsed";

  return (
    <button
      onClick={toggleSidebar}
      className="fixed top-4 z-50 flex items-center justify-center w-8 h-8 rounded-full text-white transition-all shadow-md"
      style={{
        left: `calc(${collapsed ? "var(--sidebar-width-icon, 68px)" : "var(--sidebar-width, 256px)"} - 16px)`,
        background: "#342799",
      }}
      aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
    >
      <ChevronRight
        className={`h-3.5 w-3.5 transition-transform ${collapsed ? "" : "rotate-180"}`}
      />
    </button>
  );
}

function SidebarStateSync() {
  const { state } = useSidebar();
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, state === "collapsed" ? "true" : "false");
    } catch (error) {
      console.error("Failed to save sidebar state:", error);
    }
  }, [state]);
  return null;
}

export function AppLayout({ children }: AppLayoutProps) {
  const [defaultOpen] = useState(readSidebarState);

  return (
    <TooltipProvider delayDuration={200}>
      <SidebarProvider defaultOpen={defaultOpen}>
        <SidebarStateSync />
        <div className="flex w-full bg-background">
          <AppSidebar />
          <SidebarEdgeTrigger />
          <SidebarInset
            className="min-w-0 pl-[29px] pr-6 pt-4 pb-6"
          >
            {children}
          </SidebarInset>
        </div>
      </SidebarProvider>
    </TooltipProvider>
  );
}
